import {isFeatureEnabled} from 'feature-flags';
import {match, Pattern} from 'ts-pattern';
import {Nullish} from 'utility-types';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {AvailableCarsFilterRequestBody} from '@dms/api/core';
import {CountryAlpha3CodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';

import {sourcingFilterConstants} from '../../../constants/sourcingFilterConstants';
import {UNSUPPORTED_ORIGIN_COUNTRY_CODES} from '../../../constants/unsupportedOriginCountryCodes';
import {CarFilterType} from '../types/CarFilterType';
import {FiltersType} from '../types/FiltersType';
import {SellerType} from '../types/SellerType';

export const createFilterBody = (
  filters: FiltersType,
  filterPresetId: string | Nullish,
  country: CountryAlpha3CodeEnum
): AvailableCarsFilterRequestBody => {
  const createRangeFilter = (max: number, value?: [number, number]) =>
    match([isNil(value), isNotNil(value) && value[1] === max])
      .with([true, Pattern.any], () => undefined)
      .with([false, true], () => ({gte: value![0]}))
      .with([false, false], () => ({gte: value![0], lte: value![1]}))
      .exhaustive();

  const createArrayFilter = (value?: string[]) => (isNotNilOrEmpty(value) ? value : undefined);

  const createCarObjectForServer = (): CarFilterType[] | undefined => {
    const car: CarFilterType[] = [];

    filters.make?.forEach((makeKey) => {
      const modelKeysOfThisMake =
        filters.model?.filter((modelKey) => {
          const split = modelKey.split('-');
          return split[0] === makeKey;
        }) ?? [];

      if (!modelKeysOfThisMake.length) {
        car.push({make: makeKey});
        return;
      }

      modelKeysOfThisMake.forEach((modelKey) => car.push({model: modelKey}));
    });

    return car.length ? car : undefined;
  };

  const sellerObject: SellerType = {
    country: isNotNilOrEmpty(filters.sellerCountry) ? filters.sellerCountry : undefined,
    type: isNotNilOrEmpty(filters.sellerType) ? filters.sellerType : undefined,
    server: isNotNilOrEmpty(filters.sellerServer) ? filters.sellerServer : undefined,
    avgNumberAds: filters.avgNumberAds && {
      gte: filters.avgNumberAds[0],
      lte:
        filters.avgNumberAds[1] === sourcingFilterConstants.AVG_NUMBER_ADS_MAX
          ? undefined
          : filters.avgNumberAds[1],
    },
  };

  const requestBody: AvailableCarsFilterRequestBody = {
    buyer: {country},
    seller: Object.values(sellerObject).filter((x) => x).length ? sellerObject : undefined,
    car: createCarObjectForServer(),
    year: filters.year
      ? {gte: filters.year[0], lte: filters.year[1]}
      : {gte: sourcingFilterConstants.YEAR_LIMIT_MIN},
    fuelType: createArrayFilter(filters.fuelType),
    driveType: createArrayFilter(filters.drive),
    transmission: createArrayFilter(filters.transmission),
    bodyType: createArrayFilter(filters.bodyType),
    // Combined with `bodyType` through AND. `null` means every category, which is the behaviour
    // before the category filter existed.
    vehicleCategory: filters.vehicleCategory ?? null,
    vehicleLengthCategory: createArrayFilter(filters.vehicleLengthCategory),
    vehicleHeightCategory: createArrayFilter(filters.vehicleHeightCategory),
    doorCount: createArrayFilter(filters.doorCount),
    interiorMaterial: isFeatureEnabled(featureFlags.SOURCING_INTERIOR_MATERIAL_FILTER)
      ? createArrayFilter(filters.interiorMaterial)
      : undefined,
    // A filter stored before the options were limited may still hold a code the API rejects.
    originCountry: isFeatureEnabled(featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER)
      ? createArrayFilter(
          filters.originCountry?.filter((code) => !UNSUPPORTED_ORIGIN_COUNTRY_CODES.includes(code))
        )
      : undefined,
    colors: filters.selectedColors?.length
      ? filters.selectedColors.map((x) => ({color: x.key}))
      : undefined,
    // sliders with max in infinity
    power: createRangeFilter(sourcingFilterConstants.POWER_LIMIT_MAX, filters.power),
    margin: filters.isMarginAbsolute
      ? createRangeFilter(sourcingFilterConstants.MARGIN_LIMIT_MAX, filters.margin)
      : undefined,
    marginPercent: !filters.isMarginAbsolute
      ? createRangeFilter(sourcingFilterConstants.MARGIN_PERCENT_LIMIT_MAX, filters.marginPercent)
      : undefined,
    cubicCapacity: createRangeFilter(
      sourcingFilterConstants.CUBIC_CAPACITY_LIMITS[1],
      filters.cubicCapacity
    ),
    turnover: createRangeFilter(sourcingFilterConstants.LIQUIDITY_MAX, filters.liquidity),
    pricePercentile: filters.pricePosition
      ? // Division by 100 converts percentage to decimal for API compatibility
        {gte: filters.pricePosition[0] / 100, lte: filters.pricePosition[1] / 100}
      : undefined,
    mileageRange: createRangeFilter(
      sourcingFilterConstants.MILEAGE_RANGE_MAX,
      filters.mileageRange
    ),
    daysOnStock: createRangeFilter(sourcingFilterConstants.DAYS_ON_DISPLAY, filters.daysOnDisplay),
    price: createRangeFilter(sourcingFilterConstants.PRICE_MAX, filters.priceCZK),
    numberOfSeats: createRangeFilter(
      sourcingFilterConstants.NUMBER_OF_SEATS_MAX,
      filters.numberOfSeats
    ),
    featureScore: filters.featureScore
      ? {
          // Division by 100 converts percentage to decimal for API compatibility
          gte: filters.featureScore[0] / 100,
          lte: filters.featureScore[1] / 100,
        }
      : undefined,
    vatReclaimable: filters.reclaimableVat ? filters.reclaimableVat : undefined,
    features: createArrayFilter(filters.features),
    adTitleSearchQuery: filters.adTitle ?? undefined,
    location:
      filters.address?.gps?.lat && filters.address?.gps?.lng && filters.radius
        ? {
            lat: Number.parseFloat(filters.address.gps.lat),
            lon: Number.parseFloat(filters.address.gps.lng),
            distance: filters.radius,
          }
        : undefined,
    includeSuspicious: !filters.hideSuspicious,
    onlyHitlist: filters.hitlistOnly ?? undefined,
    includeBlacklist: !filters.hideBlacklist,
    filterPresetId: filterPresetId ?? undefined,
  };

  return requestBody;
};
